// Pick one or more people from the register by typing: the list narrows as
// you type (accents and case ignored, any part of the name, email or cargo),
// arrows move, Enter picks, Esc closes. `multiple` keeps the picked ones as
// chips. value: a person id (or null), or an array of ids when multiple.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getTeamMembers } from '../api/client'
import { setNombresPersonas } from './unidades/filtrosLogica'

const norm = s => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

// Everyone active, loaded once and shared by every picker on the page.
let cache = null
export function usePersonas() {
  const [personas, setPersonas] = useState(cache || [])
  useEffect(() => {
    // People only: a fabrication team (es_equipo) is planned, never picked as a person.
    if (!cache) cache = getTeamMembers({ active: true }).then(ps => ps.filter(p => !p.es_equipo)).catch(() => [])
    Promise.resolve(cache).then(ps => {
      cache = ps
      setNombresPersonas(Object.fromEntries(ps.map(p => [p.id, p.name])))
      setPersonas(ps)
    })
  }, [])
  const porId = useMemo(() => Object.fromEntries(personas.map(p => [p.id, p])), [personas])
  return { personas, porId }
}

export default function PersonaPicker({
  value, onChange, multiple = false, personas: propias, placeholder, autoFocus = false,
  onClose, className = '', disabled = false,
}) {
  const { t } = useTranslation('common')
  const { personas: todas, porId } = usePersonas()
  const personas = propias || todas
  const elegidos = multiple ? (value || []) : (value != null ? [value] : [])
  const [q, setQ] = useState('')
  const [abierto, setAbierto] = useState(autoFocus)
  const [activo, setActivo] = useState(0)
  const caja = useRef(null)

  const opciones = useMemo(() => {
    const n = norm(q)
    return personas
      .filter(p => !(multiple && elegidos.includes(p.id)))
      .filter(p => !n || norm(p.name).includes(n) || norm(p.email).includes(n) || norm(p.role).includes(n))
      .slice(0, 50)
  }, [personas, q, multiple, elegidos])

  useEffect(() => { setActivo(0) }, [q])
  useEffect(() => {
    if (!abierto) return
    const fuera = e => { if (caja.current && !caja.current.contains(e.target)) cerrar() }
    document.addEventListener('mousedown', fuera)
    return () => document.removeEventListener('mousedown', fuera)
  })

  const cerrar = () => { setAbierto(false); setQ(''); onClose?.() }
  const elegir = p => {
    if (multiple) onChange([...elegidos, p.id])
    else { onChange(p.id, p); cerrar() }
    setQ('')
  }
  const quitar = id => onChange(multiple ? elegidos.filter(x => x !== id) : null, null)

  const onKey = e => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setAbierto(true); setActivo(i => Math.min(i + 1, opciones.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActivo(i => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); if (opciones[activo]) elegir(opciones[activo]) }
    else if (e.key === 'Escape') { e.preventDefault(); cerrar() }
    else if (e.key === 'Backspace' && !q && multiple && elegidos.length) quitar(elegidos[elegidos.length - 1])
  }

  const nombre = id => porId[id]?.name || personas.find(p => p.id === id)?.name || `#${id}`
  const unico = !multiple && value != null

  return (
    <div ref={caja} className={`relative ${className}`}>
      <div className={`flex flex-wrap items-center gap-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-sm focus-within:ring-2 focus-within:ring-gray-200 ${disabled ? 'opacity-60' : ''}`}
        onClick={() => !disabled && setAbierto(true)}>
        {multiple && elegidos.map(id => (
          <span key={id} className="inline-flex items-center gap-1 bg-gray-100 text-gray-800 rounded-full px-2 py-0.5 text-xs">
            {nombre(id)}
            {!disabled && <button type="button" onClick={e => { e.stopPropagation(); quitar(id) }}
              className="text-gray-400 hover:text-gray-700" aria-label={t('actions.remove', { defaultValue: 'Quitar' })}>×</button>}
          </span>
        ))}
        <input
          value={abierto ? q : (unico ? nombre(value) : q)}
          onChange={e => { setQ(e.target.value); setAbierto(true) }}
          onFocus={() => setAbierto(true)}
          onKeyDown={onKey}
          autoFocus={autoFocus}
          disabled={disabled}
          placeholder={(multiple && elegidos.length) ? '' : (unico && !abierto ? '' : (placeholder || t('personaPicker.placeholder', { defaultValue: 'Escribe para buscar…' })))}
          className="flex-1 min-w-[8rem] outline-none bg-transparent py-0.5"
        />
        {unico && !disabled && (
          <button type="button" onClick={e => { e.stopPropagation(); quitar(value) }}
            className="text-gray-300 hover:text-gray-600 px-1" aria-label={t('actions.clear', { defaultValue: 'Limpiar' })}>×</button>
        )}
      </div>
      {abierto && !disabled && (
        <ul className="absolute z-50 mt-1 w-full max-h-60 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg text-sm">
          {opciones.length === 0 && (
            <li className="px-3 py-2 text-gray-400">{t('personaPicker.empty', { defaultValue: 'Nadie coincide' })}</li>
          )}
          {opciones.map((p, i) => (
            <li key={p.id}
              onMouseDown={e => { e.preventDefault(); elegir(p) }}
              onMouseEnter={() => setActivo(i)}
              className={`px-3 py-1.5 cursor-pointer ${i === activo ? 'bg-gray-100' : ''}`}>
              <div className="text-gray-900">{p.name}</div>
              {(p.role || p.email) && <div className="text-[11px] text-gray-500">{[p.role, p.email].filter(Boolean).join(' · ')}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
